import React from 'react';
import { Box, ButtonBase, Typography, useMediaQuery, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';

export interface PhotosSegmentOption<T extends string> {
  value: T;
  label: string;
  /** Chữ tắt trên mobile — ví dụ Custom Frame → Frame */
  shortLabel?: string;
  icon?: React.ReactNode;
}

interface PhotosSegmentedControlProps<T extends string> {
  value: T;
  onChange: (value: T) => void;
  options: PhotosSegmentOption<T>[];
  size?: 'sm' | 'md' | 'lg';
  /** accent cho tab đang chọn */
  accentColor?: string;
  /** Một hàng full width — mỗi tab chia đều */
  fullWidth?: boolean;
  /** Chiều cao cố định (px) — căn hàng với nút bên cạnh */
  height?: number;
}

const DEFAULT_ACCENT = '#e86a8a';
const TRACK_PINK = '#ff8ba7';

function PhotosSegmentedControl<T extends string>({
  value,
  onChange,
  options,
  size = 'md',
  accentColor = DEFAULT_ACCENT,
  fullWidth = false,
  height,
}: PhotosSegmentedControlProps<T>) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const padX = size === 'sm' ? 1.25 : size === 'lg' ? 2.5 : 1.75;
  const padY = size === 'sm' ? 0.65 : size === 'lg' ? 1.1 : 0.85;
  const fontSize = size === 'sm' ? '0.78rem' : size === 'lg' ? '0.95rem' : '0.85rem';

  return (
    <Box
      role="tablist"
      sx={{
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'nowrap',
        alignItems: 'stretch',
        justifyContent: 'stretch',
        width: fullWidth ? '100%' : 'auto',
        maxWidth: '100%',
        height: height ?? 'auto',
        boxSizing: 'border-box',
        p: 0.5,
        gap: 0.5,
        borderRadius: 2.5,
        bgcolor: alpha(TRACK_PINK, 0.22),
        border: '1px solid',
        borderColor: alpha(accentColor, 0.28),
        boxShadow: `inset 0 1px 3px ${alpha(TRACK_PINK, 0.2)}`,
        ...(fullWidth
          ? {
              '& > *': {
                flex: '1 1 0',
                minWidth: 0,
              },
            }
          : null),
      }}
    >
      {options.map((opt) => {
        const selected = value === opt.value;
        return (
          <ButtonBase
            key={opt.value}
            role="tab"
            aria-selected={selected}
            aria-label={opt.label}
            onClick={() => onChange(opt.value)}
            sx={{
              display: 'flex',
              flex: fullWidth ? '1 1 0' : '0 1 auto',
              flexBasis: fullWidth ? 0 : 'auto',
              width: fullWidth ? 0 : 'auto',
              minWidth: 0,
              maxWidth: fullWidth ? '100%' : 'none',
              height: height ? '100%' : 'auto',
              justifyContent: 'center',
              alignItems: 'center',
              px: fullWidth ? { xs: 0.5, sm: 1.25, md: padX } : padX,
              py: height ? 0 : padY,
              borderRadius: 2,
              transition: 'all 0.22s cubic-bezier(0.4, 0, 0.2, 1)',
              bgcolor: selected ? '#fff' : 'transparent',
              boxShadow: selected
                ? `0 2px 10px ${alpha(accentColor, 0.22)}, 0 1px 2px ${alpha(TRACK_PINK, 0.2)}`
                : 'none',
              color: selected ? accentColor : alpha(accentColor, 0.72),
              '&:hover': {
                bgcolor: selected ? '#fff' : alpha('#fff', 0.55),
                color: accentColor,
              },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: { xs: 0, md: 0.75 },
                width: '100%',
                minWidth: 0,
              }}
            >
              {opt.icon ? (
                <Box
                  component="span"
                  sx={{
                    display: 'flex',
                    flexShrink: 0,
                    fontSize: size === 'lg' ? 20 : { xs: 20, md: 18 },
                    color: selected ? accentColor : 'inherit',
                    opacity: selected ? 1 : 0.8,
                  }}
                >
                  {opt.icon}
                </Box>
              ) : null}
              <Typography
                component="span"
                sx={{
                  display: opt.icon ? { xs: 'none', md: 'block' } : 'block',
                  fontWeight: selected ? 700 : 500,
                  fontSize: { xs: '0.72rem', sm: fontSize },
                  letterSpacing: size === 'lg' ? '0.01em' : 0,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {isMobile && opt.shortLabel ? opt.shortLabel : opt.label}
              </Typography>
            </Box>
          </ButtonBase>
        );
      })}
    </Box>
  );
}

export default PhotosSegmentedControl;
